import { faChevronsDown } from "@fortawesome/pro-regular-svg-icons/faChevronsDown";
import { faChevronsUp } from "@fortawesome/pro-regular-svg-icons/faChevronsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Avatar, Box, Grid, List, ListItem, ListItemAvatar, ListItemText, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcClickable, MprcSpinnerIndicator } from "components";
import { IconButtonEdit } from "components/iconButtons/IconButtonEdit";
import { MprcCallout } from "components/MprcCallout";
import { CalloutDto } from "generated/CoreApi_v1";
import { useInsideLayout } from "layouts/common/inside/InsideLayoutContext";
import LibUtils from "lib/LibUtils";
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

////////////////////////////////////////////////////////////

const isMenuItemSelected = (path: string, menuMatch?: string[]): string | undefined => {
    return document.location.pathname == path || menuMatch?.find(y => document.location.pathname.startsWith(y)) != null
        ? "selected"
        : undefined;
};

const GridContainer = styled(Grid)(({ theme }) => ({
    height: "100%",
    borderRight: "solid 1px",
    borderColor: theme.extended.workArea.sideBar.separatorColor,
    backgroundColor: theme.palette.common.white
}));

const CustomListItem = styled(ListItem)(({ theme }) => ({
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: 0,
    "& .MuiListItemSecondaryAction-root": {
        right: 0
    }
}));

////////////////////////////////////////////////////////////

type CollapsableState = {
    id: string;
    collapsed: boolean;
};


export type LateralBlock = {
    path: string;
    name: string;
    thumbnailUrl?: string | null;
    matchCalloutId?: string[];
};

export type CollapsableLateralMenu = {
    id: string;
    contractedHeader: string;
    collapsedHeader: string;
    initialCollapse: boolean;
};

export type LateralBlockOption = {
    items: LateralMenuItem[];
    collapsable?: CollapsableLateralMenu;
};

export type LateralMenuItem = {
    path: string;
    name: string;
    thumbnailUrl?: string | null;
    matchCalloutId?: string[];
    menuMatch?: string[];
};

export type LateralMenuBlock = {
    header?: string;
    blocks: LateralBlockOption[];
    loading?: boolean;
    onEditAction?: () => void;
};

////////////////////////////////////////////////////////////

export const LateralMenuContainer = styled('div')(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(1),
}));

////////////////////////////////////////////////////////////

export const LateralMenuList = styled(List)(({ theme }) => ({
    paddingTop: 0,
    paddingBottom: theme.spacing(1),
}));

////////////////////////////////////////////////////////////

interface ILateralMenuHeaderProps {
    title?: string;
    editAction?: () => void;
}

export const LateralMenuHeader = ({ title, editAction }: ILateralMenuHeaderProps) => {
    return (
        <Grid container justifyContent="center" alignItems="center">
            <Grid size="grow" sx={{ ml: 1 }}>
                <Typography variant="overline">{title}</Typography>
            </Grid>
            {
                editAction &&
                <Grid sx={(theme) => ({ mr: theme.spacing(-1) })}>
                    <IconButtonEdit size="small" onClick={editAction} iconSize="xs" />
                </Grid>
            }
        </Grid>
    );
};

////////////////////////////////////////////////////////////

export const LateralMenuItemSpinner = () => {
    return (
        <CustomListItem>
            <MprcSpinnerIndicator />
        </CustomListItem>
    );
};

////////////////////////////////////////////////////////////

type BuildLateralMenuItemProps = {
    path: string;
    name: string;
    thumbnailUrl?: string | null;
    menuMatch?: string[];
    callout: boolean;
};

export const BuildLateralMenuItem = ({ name, path, callout, thumbnailUrl, menuMatch }: BuildLateralMenuItemProps) => {
    return (
        <CustomListItem
            secondaryAction={
                callout
                    ? <MprcCallout />
                    : undefined
            }
        >
            {
                thumbnailUrl &&
                <ListItemAvatar sx={{ minWidth: 20 }}>
                    <Avatar
                        src={thumbnailUrl}
                        sx={(theme) => ({ width: 16, height: 16, backgroundColor: theme.palette.grey[200] })}
                    />
                </ListItemAvatar>
            }

            <ListItemText
                sx={{ mt: 0, mb: 0 }}
                primaryTypographyProps={{ noWrap: true }}
                primary={
                    <Link to={path}>
                        <Box component="span"
                            sx={
                                isMenuItemSelected(path, menuMatch)
                                    ? (theme) => ({
                                        color: theme.palette.highlight.main,
                                        fontWeight: "bold"
                                    })
                                    : undefined
                            }
                        >
                            {name}
                        </Box>
                    </Link>
                }
            />
        </CustomListItem>
    );
};

////////////////////////////////////////////////////////////

interface ILateralMenuProps {
    dataset: LateralMenuBlock[];
    callouts?: CalloutDto[];
}

function getInitialCollapsableState(dataset: LateralMenuBlock[], currentState: CollapsableState[]) {
    var d: CollapsableState[] = [];
    dataset.forEach(b => {
        b.blocks
            .filter(x => x.collapsable != undefined)
            .forEach(y => d.push({
                id: y.collapsable!.id,
                collapsed: currentState.find(p => p.id == y.collapsable!.id)?.collapsed ?? y.collapsable!.initialCollapse
            }));
    });
    return d;
}

export const LateralMenu: React.FC<ILateralMenuProps> = ({ dataset, callouts }) => {
    const location = useLocation();
    const layout = useInsideLayout();
    const [collapse, setCollapse] = useState<CollapsableState[]>([]);
    let selectedText = "";

    useEffect(() => {
        const newDs = getInitialCollapsableState(dataset, collapse);
        // Só altera se algo mudou.
        if (newDs.filter(p => collapse.findIndex(x => x.id == p.id) == -1).length > 0) {
            setCollapse(newDs);
        }
    }, [dataset]);

    useEffect(() => {
        dataset.forEach(p => {
            if (p.blocks != null) {
                p.blocks.forEach(block => {
                    if (block.items.length > 0) {
                        var item = block.items.find(x => isMenuItemSelected(x.path, x.menuMatch) == "selected");
                        if (item) {
                            selectedText = item.name;
                            return false;
                        }
                    }
                });
            }
        });

        layout.changeSubHeaderText(selectedText);
    }, [location.pathname]);

    const handleCollapse = (c: CollapsableState) => () => {
        var index = collapse.findIndex(p => p.id == c.id);
        if (index > -1) setCollapse(state => (LibUtils.arrayReplaceAt(state, index, { id: c.id, collapsed: !c.collapsed })));
    };

    return (
        <GridContainer container direction="column" flexWrap="nowrap">
            <Grid size="grow" className={AppGlobalClassKeys.thinScrollbarOver}>

                <LateralMenuContainer>
                    {
                        dataset.map((data, index) => {
                            return (
                                <React.Fragment key={index}>
                                    <LateralMenuHeader title={data.header} editAction={data.onEditAction} />
                                    <LateralMenuList dense>
                                        {
                                            data.loading
                                                ? <LateralMenuItemSpinner />
                                                : data.blocks.map((block, indexB) => {

                                                    const collapseState = block.collapsable ? collapse.find(p => p.id == block.collapsable?.id) : null;

                                                    return (
                                                        <div
                                                            key={indexB}
                                                            style={
                                                                {
                                                                    marginTop: (indexB > 0 ? 4 : 0),
                                                                    marginBottom: (index == dataset.length - 1 && indexB == data.blocks.length - 1 ? 0 : 8)
                                                                }
                                                            }
                                                        >
                                                            {
                                                                collapseState != null &&
                                                                <CustomListItem>
                                                                    <ListItemText
                                                                        sx={{ mt: 0, mb: 0 }}
                                                                        primaryTypographyProps={{ noWrap: true, color: "primary", variant: "captionHeader" }}
                                                                        primary={
                                                                            <MprcClickable onClick={handleCollapse(collapseState)}>
                                                                                <FontAwesomeIcon
                                                                                    icon={collapseState.collapsed ? faChevronsDown : faChevronsUp}
                                                                                    size="xs"
                                                                                    fixedWidth
                                                                                />
                                                                                {collapseState.collapsed ? block.collapsable?.collapsedHeader : block.collapsable?.contractedHeader}
                                                                            </MprcClickable>
                                                                        }
                                                                    />

                                                                </CustomListItem>
                                                            }

                                                            {
                                                                (collapseState == null || !collapseState.collapsed) &&
                                                                block.items.map((item, indexC) => (
                                                                    <BuildLateralMenuItem
                                                                        key={indexC}
                                                                        name={item.name}
                                                                        path={item.path}
                                                                        thumbnailUrl={item.thumbnailUrl}
                                                                        menuMatch={item.menuMatch}
                                                                        callout={(callouts ?? []).find(x => item.matchCalloutId?.includes(x.id) && x.active == true) != null}
                                                                    />
                                                                ))
                                                            }
                                                        </div>
                                                    );
                                                })
                                        }
                                    </LateralMenuList>
                                </React.Fragment>
                            );
                        })
                    }
                </LateralMenuContainer>

            </Grid>
        </GridContainer>
    );
};
